import styled from 'styled-components';
import { BlockStyleSelect } from './BlockStyleSelect';
import { Editor } from '@tiptap/react';
import MenuBarButton from './MenuBarButton';
import { useState } from 'react';
import { LinkMenu, LinkMenuMode } from './LinkMenu';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { theme } from '../../../styles/theme';
import ThinDivider from '../../ThinDivider';

interface IProps {
  editor: Editor;
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px;
  ${floatingComponentStyles};
  color: ${theme.colors.secondary800};
`;

const Divider = styled(ThinDivider)`
  margin-right: 8px;
  margin-left: 8px;
`;

export const FormattingMenu = ({ editor }: IProps) => {
  const [showLinkMenu, setShowLinkMenu] = useState(false);

  const isDisabled = !editor.isEditable;
  return (
    <>
      {!showLinkMenu && (
        <Wrapper>
          <MenuBarButton
            isActive={editor.isActive('bold')}
            disabled={
              !editor.can().chain().focus().toggleBold().run() || isDisabled
            }
            name="rte-bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            tooltip="Bold"
          />
          <MenuBarButton
            isActive={editor.isActive('italic')}
            disabled={
              !editor.can().chain().focus().toggleItalic().run() || isDisabled
            }
            name="rte-italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            tooltip="Italic"
          />
          <MenuBarButton
            isActive={editor.isActive('underline')}
            disabled={
              !editor.can().chain().focus().toggleUnderline().run() ||
              isDisabled
            }
            name="rte-underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            tooltip="Underline"
          />

          <MenuBarButton
            isActive={editor.isActive('highlight')}
            disabled={
              !editor.can().chain().focus().toggleHighlight().run() ||
              isDisabled
            }
            name="rte-highlight"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            tooltip="Highlight"
          />

          <Divider layout="vertical" size="short" />

          <MenuBarButton
            isActive={editor.isActive('textStyle', { fontSize: '120%' })}
            disabled={isDisabled}
            name="rte-font-increase"
            onClick={() => editor.chain().focus().toggleFontSize('120%').run()}
            tooltip="Increase font size"
          />
          <MenuBarButton
            isActive={editor.isActive('textStyle', { fontSize: '80%' })}
            disabled={isDisabled}
            name="rte-font-decrease"
            onClick={() => editor.chain().focus().toggleFontSize('80%').run()}
            tooltip="Decrease font size"
          />

          <Divider layout="vertical" size="short" />
          <MenuBarButton
            isActive={editor.isActive({ textAlign: 'left' })}
            disabled={
              !editor.can().chain().focus().setTextAlign('left').run() ||
              isDisabled
            }
            name="rte-left-align"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            tooltip="Left alignment"
          />
          <MenuBarButton
            isActive={editor.isActive({ textAlign: 'center' })}
            disabled={
              !editor.can().chain().focus().setTextAlign('center').run() ||
              isDisabled
            }
            name="rte-center-align"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            tooltip="Center"
          />

          <MenuBarButton
            isActive={editor.isActive({ textAlign: 'right' })}
            disabled={
              !editor.can().chain().focus().setTextAlign('right').run() ||
              isDisabled
            }
            name="rte-right-align"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            tooltip="Right alignment"
          />

          <Divider layout="vertical" size="short" />

          <MenuBarButton
            isActive={editor.isActive('bulletList')}
            disabled={isDisabled}
            name="rte-bullet-list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            tooltip="Bullet list"
          />

          <MenuBarButton
            isActive={editor.isActive('orderedList')}
            disabled={isDisabled}
            name="rte-number-list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            tooltip="Numbered list"
          />

          <MenuBarButton
            disabled={!editor.can().sinkListItem('listItem') || isDisabled}
            name="rte-indent"
            onClick={() =>
              editor.chain().focus().sinkListItem('listItem').run()
            }
            tooltip="Increase indentation"
          />

          <MenuBarButton
            disabled={!editor.can().liftListItem('listItem') || isDisabled}
            name="rte-outdent"
            onClick={() =>
              editor.chain().focus().liftListItem('listItem').run()
            }
            tooltip="Decrease indentation"
          />
          <Divider layout="vertical" size="short" />
          <BlockStyleSelect editor={editor} />
          <Divider layout="vertical" size="short" />
          <MenuBarButton
            disabled={isDisabled}
            name="rte-return"
            onClick={() => editor.chain().focus().setHardBreak().run()}
            tooltip="Line break"
          />
          <MenuBarButton
            disabled={isDisabled}
            name="rte-horizontal-line"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            tooltip="Horizontal line"
          />
          <MenuBarButton
            isActive={editor.isActive('link') || showLinkMenu}
            disabled={isDisabled}
            name={editor.isActive('link') ? 'rte-unlink' : 'rte-link'}
            onClick={() => {
              if (editor.isActive('link')) {
                editor.chain().focus().unsetLink().run();
              } else {
                setShowLinkMenu(true);
              }
            }}
            tooltip={editor.isActive('link') ? 'Remove link' : 'Link'}
          />

          <MenuBarButton
            disabled={isDisabled}
            name="rte-clear-format"
            onClick={() =>
              editor.chain().focus().clearNodes().unsetAllMarks().run()
            }
            tooltip="Clear formatting"
          />
        </Wrapper>
      )}
      {showLinkMenu && (
        <LinkMenu
          editor={editor}
          mode={LinkMenuMode.Add}
          onClose={() => {
            setShowLinkMenu(false);
            editor.chain().focus().run();
          }}
        />
      )}
    </>
  );
};
